import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import _ from 'lodash'
import { Box } from '@mui/material'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import Dropzone from 'react-dropzone'
import Loading from '../../../process-state-notifications/Loading'
import {
  getMarkdownFromSourceEditor,
  getMarkdownSourceExtensions,
  handleSourceEditorPaste,
} from '../../richtext'
import {
  applyMarkdownSyntax,
  findHighlightItemsInMarkdownSource,
  insertMarkdownImages,
  insertMarkdownTable,
} from './markdownSyntax'
import { RichTextStyleButtons } from '../../../toolbars/RichTextToolbar'
import { StyleType } from '../../../../../utils/markdown'
import { UploadedFile } from '../../../../../utils/file'

// DraftモードのショートカットキーをMarkdownソースエディタでも使えるようにする。
// StarterKit の bold/italic 等を無効化しているため、自前でキーマップを定義する。
const MarkdownKeyboardShortcuts = Extension.create({
  name: 'markdownKeyboardShortcuts',
  addKeyboardShortcuts() {
    return {
      'Mod-b': () => {
        applyMarkdownSyntax(this.editor, StyleType.BOLD)
        return true
      },
      'Mod-i': () => {
        applyMarkdownSyntax(this.editor, StyleType.ITALIC)
        return true
      },
      'Mod-Shift-x': () => {
        applyMarkdownSyntax(this.editor, StyleType.STRIKETHROUGH)
        return true
      },
      'Mod-e': () => {
        applyMarkdownSyntax(this.editor, StyleType.CODE)
        return true
      },
      'Mod-Shift-e': () => {
        applyMarkdownSyntax(this.editor, StyleType.CODE_BLOCK)
        return true
      },
      'Mod-Shift-.': () => {
        applyMarkdownSyntax(this.editor, StyleType.BLOCKQUOTE)
        return true
      },
      'Mod-Shift-1': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_ONE)
        return true
      },
      'Mod-Shift-2': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_TWO)
        return true
      },
      'Mod-Shift-3': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_THREE)
        return true
      },
      'Mod-Shift-4': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_FOUR)
        return true
      },
      'Mod-Shift-5': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_FIVE)
        return true
      },
      'Mod-Shift-6': () => {
        applyMarkdownSyntax(this.editor, StyleType.HEADER_SIX)
        return true
      },
      'Mod-Shift-h': () => {
        applyMarkdownSyntax(this.editor, StyleType.HORIZONTAL_RULE)
        return true
      },
      'Mod-Shift-8': () => {
        applyMarkdownSyntax(this.editor, StyleType.UNORDERED_LIST_ITEM)
        return true
      },
      'Mod-Shift-7': () => {
        applyMarkdownSyntax(this.editor, StyleType.ORDERED_LIST_ITEM)
        return true
      },
      // リッチテキスト側は stock TaskList 拡張のデフォルトショートカットで効くため、
      // Markdownソースエディタでも同じキーで揃える
      'Mod-Shift-9': () => {
        applyMarkdownSyntax(this.editor, StyleType.TASK_LIST)
        return true
      },
    }
  },
})

type MarkdownEditViewProps = {
  loading: boolean
  toolbarSlot: HTMLElement | null
  onDropAccepted: (acceptedFiles: File[]) => void
  onPaste: (event) => void
  onEditorReady: (editor: Editor) => void
  onEditorDestroy: () => void
  onContentChange: (markdown: string) => void
}

export const MarkdownEditView = ({
  loading,
  toolbarSlot,
  onDropAccepted,
  onPaste,
  onEditorReady,
  onEditorDestroy,
  onContentChange,
}: MarkdownEditViewProps) => {
  const [highlightItems, setHighlightItems] = useState<string[]>([])
  const [isInTable, setIsInTable] = useState(false)

  // useEditor の callback は生成時にキャプチャされるため、
  // 最新の callback 参照を ref 経由で読み直して stale 状態を避ける。
  const onContentChangeRef = useRef(onContentChange)
  onContentChangeRef.current = onContentChange

  const debouncedFindHighlight = useMemo(
    () =>
      _.debounce((ed: Editor) => {
        setHighlightItems(findHighlightItemsInMarkdownSource(ed))
      }, 100),
    []
  )

  const editor = useEditor({
    extensions: [...getMarkdownSourceExtensions(), MarkdownKeyboardShortcuts],
    content: '',
    editorProps: {
      attributes: {
        class: 'markdown-source-editor',
      },
      handlePaste: handleSourceEditorPaste,
      // 1行＝1段落の内部表現に対し、ProseMirror既定のクリップボード変換は
      // 段落間を "\n\n" で連結するため、コピペのたびに空行が増殖してしまう。
      // getMarkdownFromSourceEditor と同じ "\n" 区切りに揃える。
      clipboardTextSerializer: slice =>
        slice.content.textBetween(0, slice.content.size, '\n'),
    },
    onUpdate: ({ editor: ed }) => {
      onContentChangeRef.current(getMarkdownFromSourceEditor(ed))
      debouncedFindHighlight(ed)
      setIsInTable(
        ed.state.selection.$from.parent.textContent.trimStart().startsWith('|')
      )
    },
    onSelectionUpdate: ({ editor: ed }) => {
      debouncedFindHighlight(ed)
      setIsInTable(
        ed.state.selection.$from.parent.textContent.trimStart().startsWith('|')
      )
    },
  })

  useEffect(() => {
    if (!editor) return
    onEditorReady(editor)
    return () => {
      onEditorDestroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor参照の登録/解除だけ
  }, [editor])

  const handleStyleApply = useCallback(
    (styleType: StyleType) => {
      if (!editor) return
      applyMarkdownSyntax(editor, styleType)
    },
    [editor]
  )

  const handleTableInsert = useCallback(
    (rows: number, columns: number) => {
      if (!editor) return
      insertMarkdownTable(editor, rows, columns)
    },
    [editor]
  )

  const handleFileInsert = useCallback(
    (files: UploadedFile[]) => {
      if (!editor) return
      insertMarkdownImages(editor, files)
    },
    [editor]
  )

  return (
    <>
      {toolbarSlot &&
        createPortal(
          <RichTextStyleButtons
            editor={editor ?? undefined}
            hideHeaderFour={true}
            hideHeaderFive={true}
            hideHeaderSix={true}
            highlightItems={highlightItems}
            isInTable={isInTable}
            onStyleApply={handleStyleApply}
            onTableInsert={handleTableInsert}
            onFileInsert={handleFileInsert}
          />,
          toolbarSlot
        )}
      <Loading isLoading={loading} />
      <Dropzone onDropAccepted={onDropAccepted} multiple={true}>
        {({ getRootProps, getInputProps }) => (
          <Box
            {...getRootProps({
              onClick: e => {
                e.stopPropagation()
              },
              // Dropzone に tabIndex=0 が付くため、Box 自体がフォーカスを受け取ると
              // ブラウザの focus ring（黒枠）が出る。フォーカスを editor に転送して防ぐ。
              onFocus: () => editor?.commands.focus(),
            })}
            sx={{
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              outline: 'none',
            }}
          >
            <input {...getInputProps()} />
            <EditorContent
              editor={editor}
              onPaste={onPaste}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}
      </Dropzone>
    </>
  )
}
